/**
 * builtTabs — THE-TAB-IS-IN-THE-MENU-1. What a built tab is called, where it
 * lives, and whether a row in the map is one the menu may draw.
 *
 * ─── WHY THE MAP, AND WHY ITS OWN NAMESPACE ───────────────────────────────
 *
 * The executor recorded a tab as `system_map_nodes` address `dashboard.<slug>`
 * and nothing in the browser read that table, so a built tab could not be
 * found. The map is the right record, since it is where the executor already
 * writes what it built, but not that address: a trigger from `20261123`
 * registers every custom dashboard as `dashboard.<slug>` and names it by its
 * slug, and read live on 2026-09-16 the only two dashboard nodes in production
 * are exactly that, `mmsdhqwwjujo` and `fwwayffpvyss`. A tab beside its own
 * dashboard also hit the unique `(sub_account_id, address)` key. So a tab gets
 * `tab.<slug>`, and the menu reads that namespace and nothing else.
 *
 * `account_navigation` is not used to ADD: PLATFORM-1 made it subtract only by
 * construction, so an override can never add a node a role was denied.
 *
 * ─── A LEAF ───────────────────────────────────────────────────────────────
 *
 * No imports, because the executor is Deno and `deno check` is not the edge
 * bundler. The menu half, which needs the nav types, is `builtTabsNav.ts`.
 */

/** The map namespace a built tab lives in. */
export const TAB_PREFIX = 'tab'

/**
 * A slug as the map may hold it: lower case, anything else turned to one
 * underscore, the same normalisation `register_dashboard_node` applies. Null
 * when nothing usable is left, or when the result would be refused by the
 * map's own `address_no_id` check (six digits in a row, or sixteen hex).
 */
export function tabSlugFrom(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const slug = raw
    .toLowerCase()
    .replace(/^custom:/, '')
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
  // Every other character is already `[a-z0-9_]`, so only the first is asked.
  if (!/^[a-z]/.test(slug)) return null
  if (/[0-9]{6,}/.test(slug) || /[0-9a-f]{16,}/.test(slug)) return null
  return slug
}

/** The map address of a built tab, or null when the slug cannot be one. */
export function tabAddress(slug: unknown): string | null {
  const s = tabSlugFrom(slug)
  return s === null ? null : `${TAB_PREFIX}.${s}`
}

/** The slug inside a tab address, or null when the address is not a tab's. */
export function slugOfTabAddress(address: unknown): string | null {
  if (typeof address !== 'string' || !address.startsWith(`${TAB_PREFIX}.`)) return null
  return tabSlugFrom(address.slice(TAB_PREFIX.length + 1))
}

/**
 * THE MENU ID, which `account_navigation.node_id` can key on. Its format check
 * is `^[a-z][a-z0-9_]*$`, so no dot: a merchant can hide or rename a built tab
 * from Settings exactly as they can one of ours.
 */
export function tabNodeId(slug: string): string {
  return `${TAB_PREFIX}_${slug}`
}

/**
 * A NAME A PERSON WOULD READ, or null.
 *
 * Null when there is none, when it is the slug written out, or when it is shaped
 * like a code word: any underscore, or lower case pieces joined by dashes. THE
 * RULE FROM THE-PLAN-IS-READ-1 IS THE SAME RULE: a card that printed
 * `unbilled_work_dashboard` was a code word reaching a merchant, and a menu is
 * read far more often than a plan.
 */
export function plainTabName(raw: unknown, slug: unknown): string | null {
  if (typeof raw !== 'string') return null
  const name = raw.trim()
  if (name === '') return null
  // THE SLUG ITSELF, as stored or as filed. Not "normalises to the slug":
  // "Door to door" becomes `door_to_door` too, and it is a name.
  if (name === slug || name === tabSlugFrom(slug)) return null
  // AN UNDERSCORE ANYWHERE IS A CODE WORD. "Board crew_hours" is not a name
  // with a word in it, it is a name with an identifier in it. A dash is not:
  // "Follow-up calls" is how a person writes, so only a name that is nothing
  // but lower case pieces joined by dashes is refused.
  if (name.includes('_')) return null
  if (/^[a-z0-9]+(-[a-z0-9]+)+$/.test(name)) return null
  return name
}

/**
 * WHERE A BUILT TAB OPENS: the dashboard of the same slug, on the page that
 * holds the switcher. `Home.tsx` seeds its tab from `?dashboard=`.
 */
export function tabPath(slug: string): string {
  return `/dashboard/overview?dashboard=${encodeURIComponent(`custom:${slug}`)}`
}

/**
 * THE SAME DASHBOARD, whatever the separators. The map address can only hold
 * underscores, and the planner writes dashboard slugs with dashes, so the key a
 * tab links to and the key a dashboard was stored under are compared once
 * both are normalised, never as raw strings.
 */
export function sameDashboardKey(a: unknown, b: unknown): boolean {
  if (typeof a !== 'string' || typeof b !== 'string') return false
  if (!a.startsWith('custom:') || !b.startsWith('custom:')) return a === b
  const x = tabSlugFrom(a)
  return x !== null && x === tabSlugFrom(b)
}

/** One row of the map, as the menu's read returns it. */
export interface TabRow {
  readonly address: string
  readonly display_name: string
  readonly created_at: string
  readonly removed_at?: string | null
}

/** A built tab, ready for the menu. */
export interface BuiltTab {
  readonly id: string
  readonly slug: string
  readonly label: string
  readonly to: string
}

/**
 * THE TABS THE MENU MAY DRAW, IN THE ORDER THEY WERE BUILT.
 *
 * Only live rows in the tab namespace, only with a plain name, and each slug
 * once. Oldest first, because the owner's ruling is that the menu is what the
 * person asked for, in the order it was made; the address breaks a tie so two
 * reads of the same rows always agree.
 */
export function tabsFromMap(rows: ReadonlyArray<TabRow>): BuiltTab[] {
  const sorted = [...rows].sort(
    (a, b) => a.created_at.localeCompare(b.created_at) || a.address.localeCompare(b.address),
  )
  const seen = new Set<string>()
  const out: BuiltTab[] = []
  for (const row of sorted) {
    if (row.removed_at != null) continue
    const slug = slugOfTabAddress(row.address)
    if (slug === null || seen.has(slug)) continue
    const label = plainTabName(row.display_name, slug)
    if (label === null) continue
    seen.add(slug)
    out.push({ id: tabNodeId(slug), slug, label, to: tabPath(slug) })
  }
  return out
}

/**
 * THE PLACE A FINISH NAMES, read off a step's own proof.
 *
 * The tab branch records `menu: {where, label, to}` in its evidence. Anything
 * else is not a menu entry this build made, and says nothing.
 */
export function menuDestinationFrom(
  evidence: unknown,
): { where: 'menu'; label: string; to: string } | null {
  // Optional chaining, not a typeof check: a string or a number has no `menu`
  // either, so the check was a defence with a spare and mutation said so.
  const menu = (evidence as { menu?: unknown } | null | undefined)?.menu
  if (typeof menu !== 'object' || menu === null) return null
  const m = menu as Record<string, unknown>
  if (m.where !== 'menu') return null
  const label = typeof m.label === 'string' ? m.label.trim() : ''
  const to = typeof m.to === 'string' ? m.to.trim() : ''
  if (label === '' || to === '') return null
  return { where: 'menu', label, to }
}
